import {
  credentialsSignIn,
  googleSignIn,
  googleSignUp,
  refreshAccessToken,
} from '@/api/Auth';
import { GetOnfidoCheck } from '@/api/Onfido';
import {
  GetUserPendingCompanies,
  acceptTermsOfService,
  getCurrentUser,
} from '@/api/Users';
import {
  JWT_SECRET,
  REFRESH_TOKEN_TOLERANCE_TIME,
  SESSION_SECRET,
} from '@/constants/auth';
import { COOKIES } from '@/constants/cookies';
import { ROUTES } from '@/constants/routes';

import { User } from '@/models/User';
import { LoggingService } from '@/services/LoggingService';
import { LoginResponse } from '@/types/auth';

import {
  GOOGLE_LOGIN_CLIENT_ID,
  GOOGLE_LOGIN_CLIENT_SECRET,
} from '@treyd-io/core/constants/api';
import { serialize } from '@treyd-io/core/utils/json';
import { getExpiredTimeFromToken } from '@treyd-io/core/utils/jwt';
import { AxiosError } from 'axios';
import { getCookie } from 'cookies-next';
import { differenceInMinutes } from 'date-fns';
import { first, includes, isEmpty, isUndefined, pick, toString } from 'lodash';
import { NextApiRequest, NextApiResponse } from 'next';
import { NextAuthOptions } from 'next-auth';
import CredentialsProvider from 'next-auth/providers/credentials';
import GoogleProvider from 'next-auth/providers/google';
import { NextRequest, NextResponse } from 'next/server';
import { v4 as uuidv4 } from 'uuid';

const refreshTokenExpiredTime = 60 * 60 * 23; // 23 hours
const accessTokenToleranceTime = 600_000; // 1 minute
const { log } = new LoggingService();

export const authOptions: (
  req?: NextRequest & NextApiRequest,
  res?: NextResponse & NextApiResponse
) => NextAuthOptions = (req, res) => ({
  session: {
    strategy: 'jwt',
    maxAge: refreshTokenExpiredTime,
  },

  providers: [
    GoogleProvider({
      name: 'Google',
      clientId: `${GOOGLE_LOGIN_CLIENT_ID}`,
      clientSecret: `${GOOGLE_LOGIN_CLIENT_SECRET}`,
      authorization: {
        params: {
          response_type: 'code',
          prompt: 'select_account',
          scope:
            'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile',
        },
      },
    }),
    CredentialsProvider({
      name: 'Credentials',
      credentials: {
        email: {
          label: 'Email',
          type: 'email',
        },
        password: { label: 'Password', type: 'password' },
      },
      async authorize(credentials) {
        if (credentials?.email && credentials?.password)
          return {
            email: credentials.email,
            password: credentials.password,
          };
        return null;
      },
    }),
    CredentialsProvider({
      name: 'AccessToken Credentials',
      id: 'accessToken',
      credentials: {
        accessToken: {
          label: 'accessToken',
          type: 'accessToken',
        },
        refreshToken: {
          label: 'refreshToken',
          type: 'refreshToken',
        },
      },
      async authorize(credentials) {
        if (credentials?.accessToken)
          return {
            accessToken: credentials.accessToken,
            refreshToken: credentials.refreshToken,
          };
        return null;
      },
    }),
  ],

  callbacks: {
    async signIn({ user, account }) {
      // AccessToken Credentials
      if (
        account?.provider === 'accessToken' &&
        user?.accessToken &&
        user.refreshToken
      ) {
        account.access_token = user?.accessToken;
        account.refresh_token = user?.refreshToken;
        account.expires_at = getExpiredTimeFromToken(user?.accessToken);
        account.correlationId = uuidv4();
      }

      // Credentials
      if (account?.provider === 'credentials' && user.email && user.password) {
        const smsLastLogin = getCookie(COOKIES.SMS_LAST_LOGIN, { req, res });
        const response = await credentialsSignIn({
          email: user.email,
          password: user.password,
          sms_last_login: toString(smsLastLogin),
        }).catch((error: AxiosError<LoginResponse>) => {
          if (error.response) {
            log(
              {
                data: error.response.data,
                status: error.response.status,
                config: {
                  url: error.response.config.url,
                },
              },
              'Error in credentialsSignIn, Auth'
            );
          }

          if (error?.response?.status === 403) {
            throw new Error(
              `Too many login attempts, please try again after ${differenceInMinutes(
                new Date(toString(error?.response?.data?.locked_out_until)),
                new Date()
              )} minutes`
            );
          }
          throw new Error(error?.response?.data?.non_field_errors?.[0]);
        });

        if (response.data.transaction_id) {
          account.transactionId = response.data.transaction_id;
        } else {
          account.access_token = response.data.access_token;
          account.refresh_token = response.data.refresh_token;
          account.expires_at = getExpiredTimeFromToken(
            response.data.access_token
          );
          account.correlationId = uuidv4();
        }
      }
      // Google
      if (account?.provider === 'google' && account?.access_token && user) {
        const smsLastLogin = getCookie(COOKIES.SMS_LAST_LOGIN, { req, res });
        let response;
        try {
          // try signin first
          response = await googleSignIn({
            token: account.access_token,
            sms_last_login: toString(smsLastLogin),
          });
        } catch (e: unknown) {
          if (e instanceof AxiosError && e?.response?.status === 403) {
            log(e.response, 'Error in GoogleSignIn, Auth');
            throw new Error(
              `Too many login attempts, please try again after ${differenceInMinutes(
                new Date(e.response.data?.locked_out_until),
                new Date()
              )} minutes`
            );
          }
          // if signin failed, try signup
          response = await googleSignUp({
            token: account.access_token,
          }).catch((e: AxiosError<{ error: string }>) => {
            log(e, 'Error in GoogleSignUp, Auth');
            // if signup failed, throw error
            throw new Error(e?.response?.data?.error);
          });
          await acceptTermsOfService(response.data.access_token);
        } finally {
          if (response?.data.transaction_id) {
            account.transactionId = response.data.transaction_id;
          } else if (response) {
            // if signin or signup success, save token to account
            account.access_token = response.data.access_token;
            account.refresh_token = response.data.refresh_token;
            account.expires_at = getExpiredTimeFromToken(
              response.data.access_token
            );
            account.correlationId = uuidv4();
          }
        }
      }

      return true;
    },

    async jwt({ token, account, trigger, session }) {
      // Force update tokens in case of update (refreshClaims)
      if (trigger === 'update') {
        token.accessToken = session?.accessToken ?? token.user.accessToken;
        token.refreshToken = session?.refreshToken ?? token.user.refreshToken;
        token.correlationId = session?.correlationId ?? token.correlationId;

        if (
          !isUndefined(session?.operatorAccessToken) &&
          !isUndefined(session?.operatorRefreshToken)
        ) {
          token.operatorAccessToken = session?.operatorAccessToken;
          token.operatorRefreshToken = session?.operatorRefreshToken;
          token.impersonateUsername = session?.impersonateUsername;
        }
        // Fetch user and save it to token
        const userResponse = await getCurrentUser(token.accessToken);
        token.user = serialize(new User(userResponse.data));
        const userId = token.user?.id;
        const onfidoChecks = await GetOnfidoCheck.server(
          userId,
          token.accessToken
        );
        const userCompanies = await GetUserPendingCompanies.server(
          userId,
          token.accessToken
        );
        token.userCompanies = userCompanies.data?.lager_treyd_users;
        token.onfidoCheck = pick(
          first(onfidoChecks?.data?.lager_treyd_auth_onfidocheck),
          ['status', 'result']
        );
        return token;
      }

      // Set token after signin
      if (
        account?.expires_at &&
        account?.refresh_token &&
        account?.access_token
      ) {
        token.accessToken = account.access_token;
        token.refreshToken = account.refresh_token;
        token.expires = account.expires_at;
        // Fetch user and save it to token
        const userResponse = await getCurrentUser(token.accessToken);
        token.user = serialize(new User(userResponse.data));
        token.correlationId = account.correlationId;
        const userId = token.user?.id;
        const userCompanies = await GetUserPendingCompanies.server(
          userId,
          token.accessToken
        );
        token.userCompanies = userCompanies.data?.lager_treyd_users;
      }

      // Fetch onfido and save it to token
      if (token.user && !token.user.identity_verified) {
        const userId = token.user?.id;
        const onfidoChecks = await GetOnfidoCheck.server(
          userId,
          token.accessToken
        );
        token.onfidoCheck = pick(
          first(onfidoChecks?.data?.lager_treyd_auth_onfidocheck),
          ['status', 'result']
        );
      }

      // set transaction id to token if there is no refresh token
      if (account?.transactionId && !token?.refreshToken) {
        token.transactionId = account.transactionId;
        return token;
      } else if (token?.refreshToken) {
        const expirationDate = getExpiredTimeFromToken(token.refreshToken);
        if (Date.now() + REFRESH_TOKEN_TOLERANCE_TIME > expirationDate) {
          token.error = 'RefreshTokenExpiredError';
          return token;
        }

        // return token if it's not expired
        if (Date.now() + accessTokenToleranceTime < token.expires) return token;

        // refresh token if it's expired
        try {
          const response = await refreshAccessToken(token.refreshToken);
          if (!includes([201, 200], response.status))
            throw new Error('RefreshAccessTokenError');
          token.accessToken = response.data.access;
          token.expires = getExpiredTimeFromToken(response.data.access);
          return token;
        } catch (_e) {
          token.error = 'RefreshAccessTokenError';
          return token;
        }
      }
      return token;
    },
    async session({ session, token }) {
      if (token.transactionId && !token.refreshToken) {
        session.user = token.user;
        session.transactionId = token.transactionId;
        return session;
      }
      if (token) {
        session.user = token.user;
        session.user.accessToken = token.accessToken;
        session.user.refreshToken = token.refreshToken;
        session.operatorAccessToken = token.operatorAccessToken as string;
        session.operatorRefreshToken = token.operatorRefreshToken as string;
        session.impersonateUsername = token.impersonateUsername as string;
        session.expires = `${token.expires}`;
        session.user.error = token.error;
        session.onfidoCheck = token.onfidoCheck;
        session.error = token.error;
        session.transactionId = token.transactionId;
        session.correlationId = token.correlationId;
        session.userPendingCompanies = token.userCompanies;
      }
      if (isEmpty(token)) session.error = 'InvalidTokenError';

      return session;
    },
  },

  pages: {
    signIn: `${ROUTES.login}`,
    signOut: `${ROUTES.login}`,
    error: `${ROUTES.login}`,
  },

  secret: SESSION_SECRET,

  jwt: {
    secret: JWT_SECRET,
  },
});
